import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import FileUpload from './FileUpload';
import { formatDateISO, formatFileSize, getTodayISO } from '../lib/dateUtils';
import { FLIGHT_PHASE_OPTIONS } from '../lib/constants';
import { useAirline } from '../contexts/AirlineContext';
import { PilotCombobox } from './session/PilotCombobox';
import { OrgCombobox } from './session/OrgCombobox';
import { FleetCombobox } from './session/FleetCombobox';
import type { FleetModel } from '../hooks/useFleetModels';
import SessionTimingSection, { hhmmToMinutes, minutesToHhmm } from './session/SessionTimingSection';
import type { OrgPilot } from '../hooks/useOrgPilots';

const sessionSchema = z.object({
  // Basic Session Information
  session_date: z.string()
    .min(1, 'Session date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  // Crew — now stores org_pilot IDs or user IDs
  trainee_id: z.string().optional(),
  trainee_org_pilot_id: z.string().optional(),
  captain_id: z.string().optional(),
  captain_org_pilot_id: z.string().optional(),
  aircraft_type: z.string().min(1, 'Aircraft type is required'),

  // Timing fields (managed by SessionTimingSection) — all hh:mm strings
  briefing_start_time: z.string().optional(),
  briefing_duration: z.string().optional(),         // hh:mm, default 01:00
  session_start_time: z.string().optional(),
  session_end_time: z.string().optional(),           // auto-calculated
  sim_duration: z.string().optional(),               // hh:mm, default 04:00
  captain_pf_time: z.string().optional(),            // hh:mm
  fo_pf_time: z.string().optional(),                 // hh:mm
  debrief_duration: z.string().optional(),           // hh:mm, default 01:00

  // Location & equipment (now using OrgCombobox)
  location: z.string().optional(),
  simulator_id: z.string().optional(),
  overall_result: z.enum(['Pass', 'Partial Pass', 'Fail', '']).optional(),

  // Training Fields (flight_phase optional — deprecated, kept for pipeline compat)
  flight_phase: z.enum(FLIGHT_PHASE_OPTIONS).optional(),
  scenario_code: z.string().min(1, 'Scenario is required'),
  transcription_scenario_id: z.string().optional(),

  // Development level calibration (2026-04-19 revision - WingMentor Core Framework §6.2).
  // All optional; the API derives the effective development level via priority:
  // explicit > MPL > instructor-type > fallback.
  instructor_type: z.enum(['FI', 'CFI-I', 'MEI', 'UPRT', 'SFI', 'TRI', 'Examiner', 'TKI', '']).optional(),
  mpl_phase: z.enum(['Phase 1', 'Phase 2', 'Phase 3', 'Phase 4', '']).optional(),
  development_level: z.enum(['Beginner', 'Building Competencies', 'Proficient', 'N/A-Examiner', '']).optional(),
  trainee_profile_notes: z.string().optional(),

  // Optional Fields
  flight_route: z.string().optional(),
  special_conditions: z.string().optional(),
  weather_data: z.string().optional(),
  session_notes: z.string().optional(),
}).refine(
  (data) => data.trainee_id || data.trainee_org_pilot_id,
  { message: 'Please select a first officer', path: ['trainee_org_pilot_id'] },
);

type SessionFormData = z.infer<typeof sessionSchema>;

interface SessionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  sessionId?: string;
}

interface FileData {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  created_at: string;
}

export default function SessionForm({ onSuccess, onCancel, sessionId }: SessionFormProps) {
  const { user } = useAuth();
  const { selectedAirline } = useAirline();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<FileData[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayISO());
  const [initialFleetModel, setInitialFleetModel] = useState<FleetModel | null>(null);

  // Pilot state for PilotCombobox
  const [foOrgPilot, setFoOrgPilot] = useState<OrgPilot | null>(null);
  const [captainOrgPilot, setCaptainOrgPilot] = useState<OrgPilot | null>(null);
  const [firstPilotRole, setFirstPilotRole] = useState<'captain' | 'first_officer'>('captain');
  const [secondPilotRole, setSecondPilotRole] = useState<'captain' | 'first_officer' | 'captain_2' | 'first_officer_2'>('first_officer');

  // Trainee 1 and Trainee 2 may not both be F/O (identical crew_role would collide).
  // When Trainee 1 is F/O, disable Trainee 2's F/O option and fall back to F/O 2.
  const traineeTwoFoDisabled = firstPilotRole === 'first_officer';
  useEffect(() => {
    if (traineeTwoFoDisabled && secondPilotRole === 'first_officer') {
      setSecondPilotRole('first_officer_2');
    }
  }, [traineeTwoFoDisabled, secondPilotRole]);

  // Transcription scenarios
  const [transcriptionScenarios, setTranscriptionScenarios] = useState<{ id: string; code: string; name: string; speaker_roles: string[] }[]>([]);

  const methods = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      session_date: sessionId ? undefined : getTodayISO(),
      scenario_code: sessionId ? undefined : 'SIM_TRAINING',
      briefing_duration: sessionId ? undefined : '01:00',
      sim_duration: sessionId ? undefined : '04:00',
      debrief_duration: sessionId ? undefined : '01:00',
      flight_phase: sessionId ? undefined : 'Full Flight',
      // Unconditional, both modes. Left undefined, react-hook-form adopts
      // whatever the DOM has selected, which for a select with no empty
      // option is its first entry, so a new session was INSERTed with an
      // overall_result nobody chose. '' is the absence marker and the submit
      // handler maps it back to null.
      overall_result: '',
    },
  });

  // Prefill instructor_type from the user's profile default (new-session mode only).
  // Edit mode overrides this via the reset() call below once the session loads.
  useEffect(() => {
    if (sessionId) return; // edit mode — let the session load path set the value
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('default_instructor_type')
        .eq('user_id', user.id)
        .single();
      if (cancelled || error || !data) return;
      const d = (data.default_instructor_type as SessionFormData['instructor_type'] | null) ?? '';
      if (d) methods.setValue('instructor_type', d);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, sessionId]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = methods;

  // Derive organization_id from the selected airline (authoritative source)
  useEffect(() => {
    const loadUserOrganization = async () => {
      if (!user) return;
      if (selectedAirline?.airline_id) {
        setOrganizationId(selectedAirline.airline_id);

        // Load transcription scenarios for this org + global
        const { data: scenarios } = await supabase
          .from('transcription_scenarios')
          .select('id, code, name, speaker_roles')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (scenarios) {
          setTranscriptionScenarios(scenarios);
          // Auto-set transcription_scenario_id for new sessions with default scenario
          if (!sessionId) {
            const defaultScenario = scenarios.find((s: { code: string }) => s.code === 'SIM_TRAINING');
            if (defaultScenario) {
              setValue('transcription_scenario_id', defaultScenario.id);
            }
          }
        }
      }
    };
    loadUserOrganization();
  }, [user]);

  // Load existing session if editing
  useEffect(() => {
    if (!sessionId) {
      setInitialFleetModel(null);
      return;
    }

    const loadSession = async () => {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error loading session:', error);
        setError('Failed to load session');
        return;
      }
      if (!data) return;

      // Load crew data (supports both user_id and org_pilot_id)
      const { data: crewData } = await supabase
        .from('session_crew')
        .select('user_id, org_pilot_id, crew_role')
        .eq('session_id', sessionId);

      // First crew member: the primary pilot (first row by created_at, or first_officer/captain)
      // Second crew member: the other one
      const sorted = [...(crewData || [])].sort((a, b) => {
        // first_officer before captain, and non-_2 before _2
        const order = ['first_officer', 'captain', 'first_officer_2', 'captain_2'];
        return order.indexOf(a.crew_role) - order.indexOf(b.crew_role);
      });
      const foCrew = sorted[0] || null;
      const captainCrew = sorted[1] || null;
      if (foCrew) {
        setFirstPilotRole(foCrew.crew_role === 'captain' ? 'captain' : 'first_officer');
      }
      if (captainCrew) {
        setSecondPilotRole(captainCrew.crew_role as 'captain' | 'first_officer' | 'captain_2' | 'first_officer_2');
      }

      // Load org_pilot details if needed
      const pilotIdsToFetch = [foCrew?.org_pilot_id, captainCrew?.org_pilot_id].filter(Boolean) as string[];
      if (pilotIdsToFetch.length > 0) {
        const { data: pilots } = await supabase
          .from('org_pilots')
          .select('*')
          .in('id', pilotIdsToFetch);

        if (pilots) {
          if (foCrew?.org_pilot_id) {
            const foPilot = pilots.find((p) => p.id === foCrew.org_pilot_id);
            if (foPilot) setFoOrgPilot(foPilot as OrgPilot);
          }
          if (captainCrew?.org_pilot_id) {
            const captainPilot = pilots.find((p) => p.id === captainCrew.org_pilot_id);
            if (captainPilot) setCaptainOrgPilot(captainPilot as OrgPilot);
          }
        }
      }

      const dateOnly = data.session_date.split('T')[0];
      const toHHMM = (ts: string | null) =>
        ts ? new Date(ts).toTimeString().slice(0, 5) : '';

      // Hydrate the FleetCombobox. If the session has a real fleet_model_id
      // FK, fetch the actual row so we preserve the UUID for re-save. Otherwise
      // fall back to a text-only placeholder (id = aircraft_type text) so the
      // combobox still displays something — the submit handler guards against
      // writing a non-UUID into fleet_model_id.
      if (data.fleet_model_id) {
        const { data: fm } = await supabase
          .from('fleet_models')
          .select('id, code, name, classification')
          .eq('id', data.fleet_model_id)
          .maybeSingle();
        if (fm) {
          setInitialFleetModel(fm as FleetModel);
        } else if (data.aircraft_type) {
          setInitialFleetModel({ id: data.aircraft_type, code: data.aircraft_type, name: data.aircraft_type });
        } else {
          setInitialFleetModel(null);
        }
      } else if (data.aircraft_type) {
        setInitialFleetModel({ id: data.aircraft_type, code: data.aircraft_type, name: data.aircraft_type });
      } else {
        setInitialFleetModel(null);
      }

      reset({
        session_date: dateOnly,
        aircraft_type: data.aircraft_type,
        trainee_id: foCrew?.user_id || data.trainee_id || '',
        trainee_org_pilot_id: foCrew?.org_pilot_id || '',
        captain_id: captainCrew?.user_id || '',
        captain_org_pilot_id: captainCrew?.org_pilot_id || '',
        briefing_start_time: toHHMM(data.briefing_start_time),
        briefing_duration: data.briefing_duration_minutes ? minutesToHhmm(data.briefing_duration_minutes) : '01:00',
        session_start_time: toHHMM(data.session_start_time),
        session_end_time: toHHMM(data.session_end_time),
        sim_duration: data.sim_duration_minutes ? minutesToHhmm(data.sim_duration_minutes) : '04:00',
        captain_pf_time: data.captain_pf_minutes ? minutesToHhmm(data.captain_pf_minutes) : '',
        fo_pf_time: data.fo_pf_minutes ? minutesToHhmm(data.fo_pf_minutes) : '',
        debrief_duration: data.debrief_duration_minutes ? minutesToHhmm(data.debrief_duration_minutes) : '01:00',
        location: data.location || '',
        simulator_id: data.simulator_id || '',
        overall_result: data.overall_result || '',
        flight_phase: data.flight_phase || undefined,
        scenario_code: data.scenario_code || '',
        transcription_scenario_id: data.transcription_scenario_id || '',
        flight_route: data.flight_route || '',
        special_conditions: data.special_conditions || '',
        weather_data: data.weather_data || '',
        session_notes: data.session_notes || '',
        // Development level calibration (2026-04-19 revision).
        instructor_type: data.instructor_type || '',
        mpl_phase: data.mpl_phase || '',
        development_level: data.development_level || '',
        trainee_profile_notes: data.trainee_profile_notes || '',
      });
      setSelectedDate(dateOnly);
      loadFiles();
    };

    loadSession();
  }, [sessionId, reset, selectedAirline?.airline_id]);

  // Load files for the session (if editing)
  const loadFiles = async () => {
    if (!sessionId) return;
    setFilesLoading(true);
    try {
      const { data, error } = await supabase
        .from('files')
        .select('id, file_name, file_type, file_size, storage_path, created_at')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFiles(data || []);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleUploadComplete = async (storagePath: string, originalFileName: string) => {
    if (!sessionId || !organizationId || !user) {
      alert('Session must be saved before uploading files');
      return;
    }
    try {
      const { data: storageData } = await supabase.storage
        .from('session-files')
        .list(user.id);
      const uploadedFile = storageData?.find((f) => storagePath.includes(f.name));
      if (!uploadedFile) throw new Error('Uploaded file not found in storage');

      const ext = originalFileName.split('.').pop()?.toLowerCase();
      let fileType: 'audio' | 'text' | 'document' | 'transcript' = 'document';
      if (['mp3', 'wav', 'm4a'].includes(ext || '')) fileType = 'audio';
      else if (['txt', 'rtf'].includes(ext || '')) fileType = 'text';

      const { error } = await supabase.from('files').insert({
        organization_id: organizationId,
        session_id: sessionId,
        uploaded_by: user.id,
        file_name: originalFileName,
        file_type: fileType,
        mime_type: uploadedFile.metadata?.mimetype || 'application/octet-stream',
        file_size: uploadedFile.metadata?.size || 0,
        storage_path: storagePath,
      });
      if (error) throw error;
      await loadFiles();
      setShowUpload(false);
      alert('File uploaded successfully!');
    } catch (err) {
      console.error('Error saving file metadata:', err);
      alert(`Failed to link file: ${(err as Error).message}`);
    }
  };

  const handleDownload = async (file: FileData) => {
    try {
      const { data, error } = await supabase.storage
        .from('session-files')
        .download(file.storage_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  const handleDeleteFile = async (file: FileData) => {
    if (!confirm(`Are you sure you want to delete "${file.file_name}"?`)) return;
    try {
      const { error: storageError } = await supabase.storage
        .from('session-files')
        .remove([file.storage_path]);
      if (storageError) throw storageError;
      const { error: dbError } = await supabase.from('files').delete().eq('id', file.id);
      if (dbError) throw dbError;
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      alert('File deleted successfully');
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file');
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'audio': return '🎵';
      case 'text':
      case 'transcript': return '📄';
      default: return '📎';
    }
  };

  // Handle pilot selection from PilotCombobox
  const handleFoSelect = (pilot: OrgPilot) => {
    setFoOrgPilot(pilot);
    setValue('trainee_org_pilot_id', pilot.id, { shouldValidate: true });
    // If the pilot has a linked user, set trainee_id too
    if (pilot.linked_user_id) {
      setValue('trainee_id', pilot.linked_user_id);
    } else {
      setValue('trainee_id', '');
    }
  };

  const handleCaptainSelect = (pilot: OrgPilot) => {
    setCaptainOrgPilot(pilot);
    setValue('captain_org_pilot_id', pilot.id);
    if (pilot.linked_user_id) {
      setValue('captain_id', pilot.linked_user_id);
    } else {
      setValue('captain_id', '');
    }
  };

  const onSubmit = async (data: SessionFormData) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const toTimestamp = (date: string, time?: string) =>
        time ? new Date(`${date}T${time}:00`).toISOString() : null;

      const {
        captain_id: _captainId,
        captain_org_pilot_id: _captainOrgPilotId,
        trainee_id: _traineeId,
        trainee_org_pilot_id: _traineeOrgPilotId,
        briefing_start_time: _briefingStartTime,
        briefing_duration: _briefingDuration,
        sim_duration: _simDuration,
        captain_pf_time: _captainPfTime,
        fo_pf_time: _foPfTime,
        debrief_duration: _debriefDuration,
        ...sessionFormData
      } = data;

      // Build the session payload WITHOUT organization_id. On UPDATE we
      // intentionally leave the existing assignment alone so editing a
      // session doesn't reassign it to a different airline. On INSERT we
      // explicitly pin the new session to the currently-selected airline
      // (or fall back to the user's primary org for single-airline users).
      // Only persist fleet_model_id when the combobox actually carries a real
      // fleet_models.id UUID. The combobox may carry a text placeholder (the
      // aircraft_type string itself) for sessions loaded before this column
      // existed — writing that as an FK would fail. The DB trigger handles
      // resolution from aircraft_type text when fleet_model_id is null/absent.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const resolvedFleetModelId =
        initialFleetModel?.id && UUID_RE.test(initialFleetModel.id)
          ? initialFleetModel.id
          : null;

      const sessionData = {
        ...sessionFormData,
        briefing_start_time: toTimestamp(data.session_date, data.briefing_start_time),
        session_start_time: toTimestamp(data.session_date, data.session_start_time),
        session_end_time: toTimestamp(data.session_date, data.session_end_time),
        // Convert hh:mm durations to minutes for DB storage
        briefing_duration_minutes: hhmmToMinutes(data.briefing_duration),
        sim_duration_minutes: hhmmToMinutes(data.sim_duration),
        debrief_duration_minutes: hhmmToMinutes(data.debrief_duration),
        captain_pf_minutes: hhmmToMinutes(data.captain_pf_time),
        fo_pf_minutes: hhmmToMinutes(data.fo_pf_time),
        overall_result: data.overall_result || null,
        flight_phase: data.flight_phase || null,
        weather_data: data.weather_data || null,
        trainee_id: data.trainee_id || null,
        transcription_scenario_id: data.transcription_scenario_id || null,
        // Development level calibration fields (2026-04-19 revision). Empty string
        // -> null so the API-side derivation falls back to the next priority layer.
        instructor_type: data.instructor_type || null,
        mpl_phase: data.mpl_phase || null,
        development_level: data.development_level || null,
        trainee_profile_notes: data.trainee_profile_notes || null,
        instructor_id: user.id,
        // Only include fleet_model_id when we have a real UUID — preserves
        // an existing FK on update and lets the trigger fill in on insert.
        ...(resolvedFleetModelId ? { fleet_model_id: resolvedFleetModelId } : {}),
        updated_at: new Date().toISOString(),
      };

      let targetSessionId = sessionId;

      if (sessionId) {
        // UPDATE: preserve the session's existing organization_id. The
        // airline picker drives what the user sees, not where the session
        // is pinned. Reassigning across airlines should be an explicit
        // action, not a side-effect of saving an edit.
        const { error: updateError } = await supabase
          .from('sessions')
          .update(sessionData)
          .eq('id', sessionId);
        if (updateError) throw updateError;
      } else {
        // INSERT: pin to the currently-selected airline.
        const newOrganizationId = selectedAirline?.airline_id;
        if (!newOrganizationId) throw new Error('No airline selected. Please select an airline before creating a session.');

        const { data: insertedSession, error: insertError } = await supabase
          .from('sessions')
          .insert({
            ...sessionData,
            organization_id: newOrganizationId,
            status: 'draft' as const,
            created_at: new Date().toISOString(),
          })
          .select('id')
          .single();
        if (insertError) throw insertError;
        targetSessionId = insertedSession?.id || null;
      }

      // Upsert crew — now supports org_pilot_id
      if (targetSessionId) {
        const foPayload = {
          session_id: targetSessionId,
          user_id: data.trainee_id || null,
          org_pilot_id: data.trainee_org_pilot_id || null,
          crew_role: firstPilotRole,
        };

        const crewPayload = [
          foPayload,
          ...(data.captain_id || data.captain_org_pilot_id
            ? [{
                session_id: targetSessionId,
                user_id: data.captain_id || null,
                org_pilot_id: data.captain_org_pilot_id || null,
                crew_role: secondPilotRole,
              }]
            : []),
        ];

        // Clean slate: remove all crew rows, then re-insert the current selections.
        // This prevents stale rows when switching roles (e.g., captain → first_officer_2).
        await supabase
          .from('session_crew')
          .delete()
          .eq('session_id', targetSessionId);

        const { error: crewUpsertError } = await supabase
          .from('session_crew')
          .insert(crewPayload);
        if (crewUpsertError) throw crewUpsertError;
      }

      reset();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving session:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Session Date */}
        <div>
          <label htmlFor="session_date" className="block text-sm font-medium text-navy mb-1">
            Session Date * <span className="text-steel text-xs font-normal">(YYYY-MM-DD)</span>
          </label>
          <input
            id="session_date"
            type="date"
            {...register('session_date', {
              onChange: (e) => setSelectedDate(e.target.value),
            })}
            defaultValue={getTodayISO()}
            className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
          />
          {errors.session_date && (
            <p className="mt-1 text-sm text-red-600">{errors.session_date.message}</p>
          )}
          {selectedDate && (
            <div className="mt-2 p-2 bg-orca-elevate/5 border border-orca-elevate/20 rounded-md">
              <p className="text-xs text-steel">
                <span className="font-medium">ISO 8601 Format:</span>{' '}
                <span className="font-mono font-semibold text-navy">{selectedDate}</span>
              </p>
            </div>
          )}
        </div>

        {/* Pilot 1 */}
        <div className="flex items-end gap-3">
          <div className="shrink-0">
            <label htmlFor="first_pilot_role" className="block text-sm font-medium text-navy mb-1">Trainee 1</label>
            <select
              id="first_pilot_role"
              value={firstPilotRole}
              onChange={(e) => setFirstPilotRole(e.target.value as 'captain' | 'first_officer')}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            >
              <option value="captain">Captain</option>
              <option value="first_officer">First Officer</option>
            </select>
          </div>
          <div className="flex-1">
            <PilotCombobox
              id="first_officer"
              label={firstPilotRole === 'captain' ? 'Captain' : 'First Officer'}
              required
              onSelect={handleFoSelect}
              initialPilot={foOrgPilot}
              error={errors.trainee_org_pilot_id?.message}
            />
          </div>
        </div>

        {/* Pilot 2 (optional) */}
        <div className="flex items-end gap-3">
          <div className="shrink-0">
            <label htmlFor="second_pilot_role" className="block text-sm font-medium text-navy mb-1">Trainee 2</label>
            <select
              id="second_pilot_role"
              value={secondPilotRole}
              onChange={(e) => setSecondPilotRole(e.target.value as 'captain' | 'first_officer' | 'captain_2' | 'first_officer_2')}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            >
              <option value="first_officer" disabled={traineeTwoFoDisabled}>First Officer</option>
              <option value="captain_2">Captain 2</option>
              <option value="first_officer_2">First Officer 2</option>
            </select>
          </div>
          <div className="flex-1">
            <PilotCombobox
              id="captain"
              label={secondPilotRole === 'captain' ? 'Captain' : secondPilotRole === 'first_officer' ? 'First Officer' : secondPilotRole === 'captain_2' ? 'Captain 2' : 'First Officer 2'}
              onSelect={handleCaptainSelect}
              initialPilot={captainOrgPilot}
            />
          </div>
        </div>

        {/* Aircraft Type */}
        <FleetCombobox
          id="aircraft_type"
          label="Aircraft Type"
          required
          placeholder="e.g., Boeing 737-800"
          initialFleetModel={initialFleetModel}
          onValueChange={(value) => setValue('aircraft_type', value, { shouldValidate: true })}
          onFleetModelSelect={setInitialFleetModel}
          error={errors.aircraft_type?.message}
        />

        {/* Session Timing (for Debriefing Guide Part A) — single section per Excel spec */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-navy underline">Session Timing (for Debriefing Guide Part A)</h3>

          {/* Timing inputs with auto-calculations */}
          <SessionTimingSection />

          {/* Location & Simulator */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OrgCombobox
              id="location"
              category="location"
              label="Location or Airport code"
              placeholder="e.g., Frankfurt Training Center"
              value={methods.watch('location')}
              onValueChange={(v) => setValue('location', v)}
            />

            <OrgCombobox
              id="simulator_id"
              category="simulator_id"
              label="Aircraft or Simulator ID"
              placeholder="e.g., SIM-B737-01"
              value={methods.watch('simulator_id')}
              onValueChange={(v) => setValue('simulator_id', v)}
            />
          </div>

          {/* Overall Result */}
          <div className="md:w-1/2">
            <label htmlFor="overall_result" className="block text-sm font-medium text-navy mb-1">
              Overall Result
            </label>
            <select
              id="overall_result"
              {...register('overall_result')}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            >
              <option value="">Not set</option>
              <option value="Pass">Pass</option>
              <option value="Partial Pass">Partial Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>

          {/* Scenario + Flight Phase on same row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="scenario_code" className="block text-sm font-medium text-navy mb-1">
                Scenario <span className="text-red-500">*</span>
              </label>
              <select
                id="scenario_code"
                value={methods.watch('scenario_code') || ''}
                onChange={(e) => {
                  const code = e.target.value;
                  setValue('scenario_code', code, { shouldValidate: true });
                  // Auto-set transcription_scenario_id to match
                  const match = transcriptionScenarios.find(s => s.code === code);
                  setValue('transcription_scenario_id', match?.id || '');
                }}
                className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
              >
                <option value="">— Select scenario —</option>
                {(() => {
                  const getGroup = (code: string): string => {
                    if (code.startsWith('SIM_') || code === 'SIM_TRAINING') return 'Multi-Crew Simulator';
                    if (code.startsWith('LINE_')) return 'Line Operations';
                    if (code.startsWith('LOSA_')) return 'LOSA';
                    if (code.startsWith('IOE_')) return 'IOE';
                    if (code.startsWith('SP_SIM_')) return 'Single Pilot Simulator';
                    if (code.startsWith('SP_')) return 'Single Pilot';
                    if (code.startsWith('CLASSROOM_')) return 'Classroom';
                    return 'Other';
                  };
                  const groups: Record<string, typeof transcriptionScenarios> = {};
                  for (const sc of transcriptionScenarios) {
                    const g = getGroup(sc.code);
                    if (!groups[g]) groups[g] = [];
                    groups[g].push(sc);
                  }
                  const groupOrder = ['Multi-Crew Simulator', 'Line Operations', 'LOSA', 'IOE', 'Single Pilot', 'Single Pilot Simulator', 'Classroom', 'Other'];
                  return groupOrder
                    .filter(g => groups[g]?.length)
                    .map(g => (
                      <optgroup key={g} label={g}>
                        {groups[g]!.map(sc => (
                          <option key={sc.id} value={sc.code}>
                            {sc.name}
                          </option>
                        ))}
                      </optgroup>
                    ));
                })()}
              </select>
              {errors.scenario_code && (
                <p className="text-xs text-red-500 mt-1">{errors.scenario_code.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="flight_phase" className="block text-sm font-medium text-navy mb-1">
                Flight Phase <span className="text-steel text-xs font-normal">(optional)</span>
              </label>
              <select
                id="flight_phase"
                {...register('flight_phase')}
                className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
              >
                <option value="">— None —</option>
                {FLIGHT_PHASE_OPTIONS.map((phase) => (
                  <option key={phase} value={phase}>{phase}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Flight Route */}
          <div>
            <label htmlFor="flight_route" className="block text-sm font-medium text-navy mb-1">
              Flight Route
            </label>
            <input
              id="flight_route"
              type="text"
              {...register('flight_route')}
              placeholder="e.g., KJFK-KLAX, Training Area Alpha"
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            />
          </div>

          {/* Weather Conditions (not in Excel spec but kept per user request) */}
          <div>
            <label htmlFor="weather_data" className="block text-sm font-medium text-navy mb-1">
              Weather Conditions
            </label>
            <textarea
              id="weather_data"
              {...register('weather_data')}
              rows={2}
              placeholder="e.g., METAR, visibility, wind conditions..."
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            />
          </div>

          {/* Special Conditions */}
          <div>
            <label htmlFor="special_conditions" className="block text-sm font-medium text-navy mb-1">
              Special Conditions
            </label>
            <textarea
              id="special_conditions"
              {...register('special_conditions')}
              rows={2}
              placeholder="e.g., items that are not yet in the weather field such as non-normal configuration..."
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            />
          </div>
        </div>

        {/* Development Level (development-level-calibrated instructor assessment, 2026-04-19) */}
        <div className="space-y-4 border border-steel/20 rounded-lg p-4 bg-slate-50">
          <div>
            <h3 className="text-sm font-semibold text-navy">Development Level</h3>
            <p className="text-xs text-steel/80 mt-0.5">
              A development level describes an interim competency standard on the
              trainee&apos;s path to the final competency standard. It calibrates instruction
              and assessment expectations. It is not a grade.
            </p>
            <p className="text-xs text-steel/80 mt-0.5">
              Sets the instructor role and development level used to grade IEC3 / IEC4
              facilitation balance. Instructor Type is prefilled from your Settings default,
              override here for this session only. Leave fields blank to let the pipeline
              derive them (MPL phase {'->'} instructor type {'->'} fallback:
              Building Competencies).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="instructor_type" className="block text-sm font-medium text-navy mb-1">
                Instructor Type
              </label>
              <select
                id="instructor_type"
                {...register('instructor_type')}
                className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
              >
                <option value="">— Auto-derive —</option>
                <option value="FI">FI (Basic Flight Instructor)</option>
                <option value="CFI-I">CFI-I (Instrument)</option>
                <option value="MEI">MEI (Multi-Engine)</option>
                <option value="UPRT">UPRT (Upset Prevention)</option>
                <option value="SFI">SFI (Synthetic Flight Instructor)</option>
                <option value="TRI">TRI (Type Rating Instructor)</option>
                <option value="Examiner">Examiner (skill test / LPC / OPC)</option>
                <option value="TKI">TKI (Theoretical Knowledge)</option>
              </select>
            </div>

            <div>
              <label htmlFor="mpl_phase" className="block text-sm font-medium text-navy mb-1">
                MPL Phase <span className="text-steel text-xs font-normal">(optional)</span>
              </label>
              <select
                id="mpl_phase"
                {...register('mpl_phase')}
                className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
              >
                <option value="">— Not applicable —</option>
                <option value="Phase 1">Phase 1 (Core Flying Skills)</option>
                <option value="Phase 2">Phase 2 (Basic)</option>
                <option value="Phase 3">Phase 3 (Intermediate)</option>
                <option value="Phase 4">Phase 4 (Advanced / Line-Oriented)</option>
              </select>
            </div>

            <div>
              <label htmlFor="development_level" className="block text-sm font-medium text-navy mb-1">
                Development level <span className="text-steel text-xs font-normal">(override)</span>
              </label>
              <select
                id="development_level"
                {...register('development_level')}
                className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
              >
                <option value="">— Auto-derive —</option>
                <option value="Beginner">Beginner (70% instruction / 30% facilitation)</option>
                <option value="Building Competencies">Building Competencies (40 / 60)</option>
                <option value="Proficient">Proficient (10 / 90)</option>
                <option value="N/A-Examiner">N/A (Examiner evaluation)</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="trainee_profile_notes" className="block text-sm font-medium text-navy mb-1">
              Trainee Profile Notes <span className="text-steel text-xs font-normal">(optional)</span>
            </label>
            <textarea
              id="trainee_profile_notes"
              {...register('trainee_profile_notes')}
              rows={2}
              placeholder='e.g., "first jet transition", "returning after 90-day lay-off", "flagged for SAW development"'
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            />
          </div>
        </div>

        {/* Session Notes */}
        <div>
          <label htmlFor="session_notes" className="block text-sm font-medium text-navy mb-1">
            Session Notes
          </label>
          <textarea
            id="session_notes"
            {...register('session_notes')}
            rows={3}
            placeholder="Additional notes about the session..."
            className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
          />
        </div>

        {/* Files Section (only when editing) */}
        {sessionId && (
          <div className="pt-6 border-t border-steel/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-navy">Session Files</h3>
              <button
                type="button"
                onClick={() => setShowUpload(!showUpload)}
                className="px-3 py-1 text-sm font-medium text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
              >
                {showUpload ? 'Cancel Upload' : '+ Upload File'}
              </button>
            </div>

            {showUpload && (
              <div className="mb-4 p-4 bg-white rounded-lg border border-steel/20">
                <FileUpload onUploadComplete={handleUploadComplete} acceptedFileTypes="all" />
              </div>
            )}

            {filesLoading ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orca-elevate"></div>
                <p className="mt-2 text-sm text-steel">Loading files...</p>
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-6 bg-white rounded-lg border border-steel/20">
                <p className="text-sm text-steel">No files uploaded yet</p>
                <p className="text-xs text-steel/70 mt-1">Upload audio or transcript files for this session</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border border-steel/20 rounded-md hover:bg-ice/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-xl">{getFileIcon(file.file_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy truncate">{file.file_name}</p>
                        <p className="text-xs text-steel">
                          {formatFileSize(file.file_size)} • {file.file_type} • {formatDateISO(file.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-3">
                      <button
                        type="button"
                        onClick={() => handleDownload(file)}
                        className="px-2 py-1 text-xs font-medium text-orca-elevate hover:text-orca-elevate/80 transition-colors"
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(file)}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-steel hover:text-navy border border-steel/30 rounded-md transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : sessionId ? 'Update Session' : 'Create Session'}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
